import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { IGNITE_CONSULT_URL, PAPERFORM_URL } from "@/lib/leads";

/**
 * The HR Connect global CTA pattern (2-element):
 *  - Primary (large, high contrast): "Get Your Free Benefits Assessment" → Paperform
 *  - Supporting (small text link): "Already know what you need? Book a consult directly with Ignite Health →"
 *
 * Same-page anchors ("#video") render as <a>; cross-page routes render as <Link>.
 */
export default function CTAGroup({
    align = "left",
    primaryLabel = "Get Your Free Benefits Assessment",
    primaryHref = PAPERFORM_URL,
    className = "",
    testIdPrefix = "cta",
}) {
    const alignment = align === "center" ? "items-center text-center" : "items-start";
    const rowAlign = align === "center" ? "justify-center" : "justify-start";

    const isSameAnchor = primaryHref.startsWith("#");
    const isExternal = primaryHref.startsWith("http");

    const primaryClass =
        "group inline-flex items-center gap-2.5 bg-brand text-brand-foreground hover:bg-brand-hover px-8 py-4 rounded-sm text-base font-semibold tracking-wide transition-colors shadow-sm hover:shadow";

    const primaryContent = (
        <>
            {primaryLabel}
            <ArrowUpRight
                className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                strokeWidth={1.75}
            />
        </>
    );

    return (
        <div className={`flex flex-col gap-4 ${alignment} ${className}`}>
            <div className={`flex flex-wrap gap-3 ${rowAlign}`}>
                {isSameAnchor || isExternal ? (
                    <a
                        href={primaryHref}
                        {...(isExternal && { target: "_blank", rel: "noreferrer" })}
                        data-testid={`${testIdPrefix}-primary`}
                        className={primaryClass}
                    >
                        {primaryContent}
                    </a>
                ) : (
                    <Link
                        to={primaryHref}
                        data-testid={`${testIdPrefix}-primary`}
                        className={primaryClass}
                    >
                        {primaryContent}
                    </Link>
                )}
            </div>
            <a
                href={IGNITE_CONSULT_URL}
                target="_blank"
                rel="noreferrer"
                data-testid={`${testIdPrefix}-supporting`}
                className="text-sm text-ink-soft hover:text-brand transition-colors"
            >
                Already know what you need?{" "}
                <span className="underline underline-offset-4 decoration-border">
                    Book a consult directly with Ignite Health
                </span>{" "}
                →
            </a>
        </div>
    );
}
